import React from 'react';

/**
 * ChallengeTag Component
 * 
 * Displays a styled tag above challenges to indicate their type or purpose.
 * 
 * Usage in lessons.json:
 * Add a "tag" field to any challenge object with one of these values:
 * - "CHALLENGE" - For difficult or important challenges
 * - "REVIEW" - For review/recap challenges
 * - "CONCEPT" - For concept-focused challenges
 * 
 * Example:
 * {
 *   "type": "MultiChoice",
 *   "tag": "CHALLENGE",
 *   "question": ["..."],
 *   ...
 * }
 */

// Predefined tag configurations with icons and styling
const TAG_CONFIGS = {
    CHALLENGE: {
        label: 'CHALLENGE',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        bgColor: 'bg-[#FF6B6B]',
        textColor: 'text-white',
        borderColor: 'border-[#FF5252]'
    },
    REVIEW: {
        label: 'REVIEW',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        bgColor: 'bg-[hsl(165,61%,47%)]',
        textColor: 'text-white',
        borderColor: 'border-[hsl(174,51%,37%)]'
    },
    CONCEPT: {
        label: 'CONCEPT',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" />
                <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
        bgColor: 'bg-[#5C7CFA]',
        textColor: 'text-white',
        borderColor: 'border-[#4C6EF5]'
    }
};

function ChallengeTag({ tag }) {
    // If no tag is provided, don't render anything
    if (!tag) return null;

    // Get the configuration for this tag type
    const config = TAG_CONFIGS[tag.toUpperCase()];

    // If tag type is not recognized, don't render
    if (!config) {
        return null;
    }

    return (
        <div
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${config.bgColor} ${config.textColor} border-2 ${config.borderColor} font-bold text-xs shadow-sm`}
            style={{ animation: 'tagFadeIn 300ms ease-out' }}
        >
            {config.icon}
            <span>{config.label}</span>
        </div>
    );
}

export default ChallengeTag;
